/**
 * FERP Portal - Login Page
 *
 * Entry point for authentication flow.
 * Redirects authenticated users to dashboard.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Stack,
  Center,
  Loader,
  Alert,
} from '@mantine/core';
import { IconLogin, IconAlertCircle } from '@tabler/icons-react';

import { useAuth } from '@/auth/AuthContext';

export function LoginPage() {
  const { isAuthenticated, isLoading, login, error } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <Center h="100vh">
        <Stack ta="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text size="sm" c="dimmed">Checking authentication...</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Container size={420} my={80}>
      <Title ta="center" order={1} mb="xs">
        FERP Portal
      </Title>
      <Text c="dimmed" size="sm" ta="center" mb={30}>
        Life-Critical Workflow Engine
      </Text>

      <Paper withBorder shadow="md" p={30} mt={30} radius="md">
        <Stack>
          <Text size="sm" ta="center" c="dimmed">
            Sign in with your organization credentials
          </Text>

          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Authentication Error"
              color="red"
              variant="light"
            >
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            leftSection={<IconLogin size={18} />}
            onClick={login}
            size="md"
          >
            Sign in with SSO
          </Button>

          <Text size="xs" c="dimmed" ta="center">
            Protected by Authentik OIDC + AWS IAM
          </Text>
        </Stack>
      </Paper>

      <Text c="dimmed" size="xs" ta="center" mt={20}>
        FERP v0.1.0 • Domain-Agnostic Portal
      </Text>
    </Container>
  );
}
